import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  breakpointAnalysisUrl,
  captableUrl,
  cashFlowLedgerUrl,
  financialUrl,
  firmSummaryUrl,
  fundOwnershipUrl,
  valuationUrl,
} from 'common/config/urls';
import {
  PDF_EXPORT_QUERY_PARAM_EXPORT_DESCRIPTION_PAGE,
  PDF_EXPORT_QUERY_PARAM_EXPORT_MODE,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TAB,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET,
} from 'common/constants/pdfExport';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { ValuationByAllocation } from 'common/types/valuation';
import { DESCRIPTIONS_PER_PAGE, PdfExportTargets } from 'pages/Valuations/util/constants';
import {
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_PUBLIC_COMPANIES_APPROACH,
  VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { toPdfExportTabId } from 'pages/ValuationsAllocation/util';
import {
  CompanyPdfOptions,
  PdfExportConfiguration,
  PdfExportOption,
} from '../PdfExportConfigDialog.types';
import { PdfExportSupportedQueryParams, PdfExportTargetMapping, PdfPage } from './types';

const withQuery: (path: string, params: PdfExportSupportedQueryParams) => string = (path, params) => {
  const url = new URL(path, window.location.origin);
  if (params.date) {
    url.searchParams.set('date', params.date);
  }
  if (params.version) {
    url.searchParams.set('version', params.version);
  }
  if (params.exportMode) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_MODE, params.exportMode);
  }
  if (params.exportTarget) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET, params.exportTarget);
  }
  if (params.exportTab) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_TAB, params.exportTab);
  }
  if (params.exportDescriptionPage) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_DESCRIPTION_PAGE, params.exportDescriptionPage);
  }
  return url.toString();
};

/**
 * Hook to build dynamic URLs for PDF export based on current context and selected configuration
 */
export const usePdfPages = (
  configuration: PdfExportConfiguration,
  valuationInfo?: ValuationByAllocation | null
): PdfPage[] => {
  const location = useLocation();
  const params = useParams();
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo } = storeValues;

  return useMemo(() => {
    // Build URLs dynamically based on current context and configuration
    const firmSlug = firmInfo?.slug || params.firmSlugParam;
    const companySlug = companyInfo?.slug || params.companySlugParam;
    // Extract current query parameters
    const currentSearchParams = new URLSearchParams(location.search);
    const measurementDate = currentSearchParams.get('date');
    const version = currentSearchParams.get('version');

    // Common query params
    const commonQuery = {
      date: measurementDate,
      version,
      exportMode: 'true',
    } as const;

    // Build URLs based on selected configuration
    const pages: PdfPage[] = [];
    const valuationApproaches = valuationInfo?.valuations_approaches ?? [];

    const pushPage = (
      builder: (fs: string, cs: string) => string,
      title: string,
      exportTarget: string,
      exportTab?: string,
      exportDescriptionPage?: string
    ) => {

      pages.push({
        url: withQuery(builder(firmSlug!, companySlug!), {
          ...commonQuery,
          exportTarget,
          exportTab,
          exportDescriptionPage,
        }),
        title,
        exportTarget,
        exportTab,
      });
    };

    const companyOptionMapping: Record<
      keyof CompanyPdfOptions,
      Array<{
        urlBuilder: (fs: string, cs: string) => string;
        target: PdfExportTargetMapping;
        exportDescriptionPage?: string;
      }>
    > = {
      valuationSummary: [{ urlBuilder: valuationUrl, target: PdfExportTargets.VALUATION_SUMMARY }],
      equityAllocation: [{ urlBuilder: valuationUrl, target: PdfExportTargets.EQUITY_ALLOCATION }],
      concludedShareValues: [{ urlBuilder: valuationUrl, target: PdfExportTargets.CONCLUDED_SHARE_VALUES }],
      financials: [{ urlBuilder: financialUrl, target: PdfExportTargets.FINANCIALS }],
      capTable: [{ urlBuilder: captableUrl, target: PdfExportTargets.CAP_TABLE }],
      fundOwnership: [{ urlBuilder: fundOwnershipUrl, target: PdfExportTargets.FUND_OWNERSHIP }],
      breakpointAnalysis: [{ urlBuilder: breakpointAnalysisUrl, target: PdfExportTargets.BREAKPOINT_ANALYSIS }],
      cashFlowLedger: [{ urlBuilder: cashFlowLedgerUrl, target: PdfExportTargets.CASH_FLOW_LEDGER }],
      approachBacksolve: valuationApproaches
        .filter(
          a =>
            a.approach_type === VALUATIONS_BACKSOLVE_APPROACH &&
            (a.valuations_approach_backsolve?.applied_methodologies?.length ?? 0) > 0
        )
        .map(a => {
          const approachTab = toPdfExportTabId('approach', a.approach_type, a.id ?? a.panelId);
          const approachTitle = a.name ?? PdfExportTargets.BACKSOLVE.title;
          return {
            urlBuilder: valuationUrl,
            target: { title: approachTitle, id: PdfExportTargets.BACKSOLVE.id, tab: approachTab },
          };
        }),
      approachBacksolveMarketAdjustment: valuationApproaches
        .filter(
          a =>
            a.approach_type === VALUATIONS_BACKSOLVE_APPROACH &&
            (a.valuations_approach_backsolve?.applied_methodologies?.length ?? 0) > 0 &&
            a.valuations_approach_backsolve?.adjust_for_market
        )
        .map(a => {
          const approachTab = toPdfExportTabId('approach', a.approach_type, a.id ?? a.panelId);
          const approachTitle = a.name ?? PdfExportTargets.BACKSOLVE.title;
          return {
            urlBuilder: valuationUrl,
            target: {
              title: `Market Adjustment Ledger - ${approachTitle}`,
              id: PdfExportTargets.BACKSOLVE_MARKET_ADJUSTMENT_LEDGER.id,
              tab: approachTab,
            },
          };
        }),
      approachDcf: [{ urlBuilder: valuationUrl, target: PdfExportTargets.DISCOUNTED_CASH_FLOW }],
      approachExternalValuation: [{ urlBuilder: valuationUrl, target: PdfExportTargets.EXTERNAL_VALUATION }],
      approachFutureExit: [{ urlBuilder: valuationUrl, target: PdfExportTargets.FUTURE_EXIT }],
      approachPublicComps: [{ urlBuilder: valuationUrl, target: PdfExportTargets.PUBLIC_COMPS }],
      approachPublicCompsDescriptions: valuationApproaches
        .filter(a => a.approach_type === VALUATIONS_PUBLIC_COMPANIES_APPROACH)
        .flatMap(a => {
          const approachTab = toPdfExportTabId('approach', a.approach_type, a.id ?? a.panelId);
          const approachTitle = a.name ?? PdfExportTargets.PUBLIC_COMPS.title;
          const descriptionsCount = a.valuations_approach_gpc?.gpc_comparison?.length ?? 0;
          const pageCount = Math.ceil(descriptionsCount / DESCRIPTIONS_PER_PAGE);
          if (pageCount === 0) {
            return [];
          }

          return Array.from({ length: pageCount }, (_, index) => ({
            urlBuilder: valuationUrl,
            target: {
              title: `${approachTitle} - Descriptions - Page ${index + 1} of ${pageCount}`,
              id: PdfExportTargets.PUBLIC_COMPS_DESCRIPTIONS.id,
              tab: approachTab,
            },
            exportDescriptionPage: String(index),
          }));
        }),
      approachTransactionComps: [{ urlBuilder: valuationUrl, target: PdfExportTargets.TRANSACTION_COMPS }],
      approachTransactionCompsDescriptions: valuationApproaches
        .filter(a => a.approach_type === VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH)
        .flatMap(a => {
          const approachTab = toPdfExportTabId('approach', a.approach_type, a.id ?? a.panelId);
          const approachTitle = a.name ?? PdfExportTargets.TRANSACTION_COMPS.title;
          const descriptionsCount = a.valuations_approach_gpt?.gpt_transactions?.length ?? 0;
          const pageCount = Math.ceil(descriptionsCount / DESCRIPTIONS_PER_PAGE);
          if (pageCount === 0) {
            return [];
          }

          return Array.from({ length: pageCount }, (_, index) => ({
            urlBuilder: valuationUrl,
            target: {
              title: `${approachTitle} - Descriptions - Page ${index + 1} of ${pageCount}`,
              id: PdfExportTargets.TRANSACTION_COMPS_DESCRIPTIONS.id,
              tab: approachTab,
            },
            exportDescriptionPage: String(index),
          }));
        }),
      approachSpecifiedShareValues: [{ urlBuilder: valuationUrl, target: PdfExportTargets.SPECIFIED_SHARE_VALUES }],
      approachCalibration: [{ urlBuilder: valuationUrl, target: PdfExportTargets.CALIBRATION }],
      approachRangeSensitivity: [{ urlBuilder: valuationUrl, target: PdfExportTargets.RANGE_SENSITIVITY }],
    };

    switch (configuration.selectedOption) {
      case PdfExportOption.VALUATIONS:
        if (companySlug) {
          const { companyOptions, companyOptionsOrder } = configuration;
          companyOptionsOrder.forEach(key => {
            if (companyOptions[key] && companyOptionMapping[key]) {
              companyOptionMapping[key].forEach(({ urlBuilder, target, exportDescriptionPage }) => {
                pushPage(
                  urlBuilder,
                  target.title,
                  target.id,
                  'tab' in target ? target.tab : undefined,
                  exportDescriptionPage
                );
              });
            }
          });
        }
        break;

      case PdfExportOption.FIRM:
        if (firmSlug) {
          const { firmOptions } = configuration;

          if (firmOptions.summary) {
            pages.push({
              url: withQuery(firmSummaryUrl(firmSlug!), {
                ...commonQuery,
                exportTarget: PdfExportTargets.FIRM_SUMMARY.id,
              }),
              title: PdfExportTargets.FIRM_SUMMARY.title,
              exportTarget: PdfExportTargets.FIRM_SUMMARY.id,
            });
          }
        }
        break;

      default:
        // No additional URLs for unknown options
        break;
    }

    return pages;
  }, [configuration, location.search, params, companyInfo, firmInfo, valuationInfo]);
};
